import styled from "@emotion/styled";
import { border } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { themed } from "@smartsheet/sds-theme-internal";
import { focusRingTheme } from "../../../util/createFocusRingStyles";
import { withFocusToggleClassName } from "../../../util/withFocusClassName";
import { SHOW_FOCUS_MARKER } from "../../focusMode/hooks/useFocusMode";
import {
    TextInputRootBoxProps,
    formInputTheme,
    getBackgroundColor,
    getBorderColor,
    getBorderWidth,
    getStylingState,
} from "../../textinput/styled/formInputTheme";

// These are used to derive the fact that a 4 line textarea with
// box-sizing: border-box is exactly 94px high, so we can avoid
// magic numbers.
export const VERTICAL_PADDING = 11;
export const LINE_HEIGHT_PX = 18;
export const SIZING_BUFFER = 1;

interface TextAreaStyledProps extends TextInputRootBoxProps {
    disableManualResize: boolean;
}

const textAreaTheme = themed({
    verticalPadding: {
        rest: {
            "lodestar-light": VERTICAL_PADDING,
            "ursa-light": VERTICAL_PADDING - 5,
        },
        active: {
            "lodestar-light": VERTICAL_PADDING,
            "ursa-light": VERTICAL_PADDING - 6,
        },
        error: {
            "lodestar-light": VERTICAL_PADDING,
            "ursa-light": VERTICAL_PADDING - 6,
        },
    },
    horizontalPadding: {
        rest: {
            "lodestar-light": 12,
            "ursa-light": 8,
        },
        active: {
            "lodestar-light": 12,
            "ursa-light": 7,
        },
        error: {
            "lodestar-light": 12,
            "ursa-light": 7,
        },
    },
});

export const TextAreaStyled = withFocusToggleClassName(styled("textarea")<TextAreaStyledProps>`
    border-style: solid;
    border-width: ${getBorderWidth}px;
    border-color: ${getBorderColor};
    border-radius: ${({ themeMode }) => formInputTheme.borderRadius(themeMode)};
    background-color: ${getBackgroundColor};

    // If inline, this will include a bit of weird height at the bottom.
    display: block;

    padding: ${({ themeMode, validationState, isFocused, focusMode }) => `
        ${textAreaTheme.verticalPadding[getStylingState({ validationState, isFocused, focusMode })](themeMode)}px
        ${textAreaTheme.horizontalPadding[getStylingState({ validationState, isFocused, focusMode })](themeMode)}px
        ${
            textAreaTheme.verticalPadding[getStylingState({ validationState, isFocused, focusMode })](themeMode) -
            SIZING_BUFFER
        }px
    `};
    width: 100%;
    box-sizing: border-box;
    transition: background-color 100ms ease-in-out;
    resize: ${(props) => (props.disableManualResize ? "none" : "vertical")};
    color: ${color("color--text--neutral--strong")};

    /* Font */
    font-size: ${({ themeMode }) => formInputTheme.fontSize.box(themeMode)};
    font-weight: ${({ themeMode }) => formInputTheme.fontWeight(themeMode)};
    line-height: ${LINE_HEIGHT_PX}px;
    font-family: inherit;
    &::placeholder {
        color: ${({ themeMode }) => formInputTheme.placeholderColor.rest(themeMode)};
    }

    &:hover::placeholder {
        color: ${({ themeMode }) => formInputTheme.placeholderColor.hover(themeMode)};
    }

    // Some high-contrast modes turn off box-shadows. In that case, we'll want to allow those HC
    // stylesheets to provide their own outline colors rather than turning off outlines entirely.
    outline: 3px solid transparent;
    body.${SHOW_FOCUS_MARKER} &:focus {
        ${getFocusStyles}
    }
`);

function getFocusStyles({ themeMode, validationState }: TextAreaStyledProps): string {
    // Normally we'd use createFocusRingStyles here but we can't do that here since
    // you cannot use a ::before element in a textarea.
    switch (themeMode) {
        case "ursa-light":
        case "ursa-dark":
            return `
                box-shadow:
                    0 0 0 ${border.width10} ${focusRingTheme.innerColor(themeMode)},
                    0 0 0 calc(1px + ${border.width20}) ${focusRingTheme.outerColor(themeMode)}
                ;
            `;
        default:
            return `
                border-color: ${
                    validationState === "error"
                        ? formInputTheme.borderColor.error(themeMode)
                        : formInputTheme.borderColor.focus(themeMode)
                };
            `;
    }
}
